import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Check, Star, BookOpen, Shield, Users, Palette, Smartphone, Lock, DollarSign, Sparkles, Gift, Wallet } from 'lucide-react';

const li = { display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '1rem', color: 'var(--lv-ink)', fontSize: '0.95rem' };
const ico = { color: 'var(--lv-accent)', marginTop: '2px' };
const FEATURES = [
  [Check, 'pricing-feature-unlimited', <><strong>Publicações ilimitadas:</strong> publique livros e periódicos em segundos! Basta enviar seus arquivos PDF, que são automaticamente convertidos em um ambiente de leitura intuitivo, interativo e repleto de recursos para uma experiência diferenciada.</>],
  [Sparkles, 'pricing-ai-highlight', <><strong>Catalogação automática com IA:</strong> simplifique a inclusão e a organização das publicações.</>],
  [Shield, 'pricing-feature-antipiracy', <><strong>Proteção antipirataria:</strong> recursos para restringir capturas de tela e cópias, conforme o dispositivo.</>],
  [Users, 'pricing-feature-subscribers', <><strong>Gestão de assinantes e acessos:</strong> acompanhe e gerencie seus assinantes e seus respectivos acessos de forma simples e centralizada.</>],
  [Wallet, 'pricing-feature-finance', <><strong>Gestão financeira automatizada:</strong> deixe toda a operação financeira com a Livrum! A plataforma processa os pagamentos, libera automaticamente os acessos e gerencia os repasses mensais para sua conta. Você só precisa definir o preço da assinatura e pode acompanhar tudo com relatórios e métricas em tempo real.</>],
  [Palette, 'pricing-feature-identity', <><strong>Catálogo com a sua identidade:</strong> tenha seu próprio espaço digital, personalizado com a logomarca da sua editora e disponível em um endereço exclusivo: <span data-testid="pricing-catalog-url" style={{ whiteSpace: 'nowrap' }}>livrum.com.br/<strong style={{ fontWeight: 800, color: 'var(--lv-accent-strong)' }}>suaeditora</strong></span>.</>],
];

const PricingSection = () => {
  const navigate = useNavigate();

  const handleSelectPlan = () => {
    sessionStorage.setItem('selectedPlan', JSON.stringify({
      id: 'editor_anual',
      title: 'Plano Editora',
      price: 'R$ 3.000',
      libraryType: 'digital'
    }));
    navigate('/cadastro');
  };

  return (
    <section className="pricing-section" id="planos">
      <div className="pricing-container">
        {/* Descrição */}
        <div style={{
          textAlign: 'center',
          marginBottom: '2rem',
          padding: '1.5rem 2rem',
          background: 'var(--lv-accent-soft)',
          borderRadius: '16px',
          maxWidth: '800px',
          margin: '0 auto 2.5rem'
        }}>
          <p style={{ color: 'var(--lv-ink)', margin: 0, fontSize: '1.1rem', lineHeight: '1.7' }}>
            Publique seus <strong style={{ color: 'var(--lv-accent)' }}>livros e periódicos</strong> em uma plataforma segura. 
            Defina o valor da assinatura e ofereça uma <strong style={{ color: 'var(--lv-accent)' }}>experiência de leitura premium</strong> aos seus assinantes.
          </p>
        </div>

        {/* Card único de plano */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center',
          gap: '2rem',
          flexWrap: 'wrap'
        }}>
          <Card 
            className="pricing-card popular"
            style={{
              position: 'relative',
              border: '1px solid var(--lv-line)',
              maxWidth: '420px',
              width: '100%'
            }}
          >
            <div style={{
              position: 'absolute',
              top: '-12px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'linear-gradient(135deg, var(--lv-accent) 0%, var(--lv-accent) 100%)',
              color: 'white',
              padding: '0.35rem 1.25rem',
              borderRadius: '50px',
              fontSize: '0.8rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem'
            }}>
              <Star className="w-4 h-4" />
              Plano Único
            </div>
            
            <CardHeader className="text-center" style={{ paddingTop: '2rem' }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, var(--lv-accent) 0%, var(--lv-accent) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}>
                <BookOpen className="w-8 h-8" style={{ color: 'white' }} />
              </div>
              <CardTitle className="pricing-card-title" style={{ fontSize: '1.5rem' }}>
                Plano Editora
              </CardTitle>
              <p data-testid="pricing-subtitle" style={{ color: 'var(--lv-muted)', fontSize: '0.95rem', marginTop: '0.5rem' }}>
                Seu catálogo digital, com publicações ilimitadas.
              </p>
            </CardHeader>
            
            <CardContent className="text-center">
              <div className="pricing-card-price" style={{ marginBottom: '0.5rem' }}>
                <span className="price" style={{ fontSize: '2.5rem', fontWeight: '700', color: 'var(--lv-ink)' }}>R$ 3.000</span>
                <span className="period" style={{ color: 'var(--lv-muted)' }}>/ano</span>
              </div>
              
              <p style={{ 
                color: 'var(--lv-accent)', 
                fontWeight: '600', 
                fontSize: '1rem',
                marginBottom: '1.5rem',
                padding: '0.75rem',
                background: 'var(--lv-accent-soft)',
                borderRadius: '8px'
              }}>
                + 30% sobre o valor de cada assinatura vendida
              </p>
              <p data-testid="pricing-payment-options" style={{ color: 'var(--lv-ink)', fontSize: '0.92rem', marginBottom: '1rem', padding: '0.7rem', background: 'rgba(56, 189, 248, 0.08)', border: '1px solid rgba(56, 189, 248, 0.25)', borderRadius: '8px' }}>
                <strong>Pagamento:</strong> à vista no <strong>Pix</strong> ou em até <strong>4x de R$&nbsp;750</strong> no cartão de crédito.
              </p>
              <p data-testid="pricing-trial-highlight" style={{ color: '#F6C76B', fontWeight: 700, fontSize: '0.95rem', marginBottom: '1rem', padding: '0.7rem', background: 'rgba(246, 199, 107, 0.1)', border: '1px solid rgba(246, 199, 107, 0.25)', borderRadius: '8px', display: 'flex', gap: 8, alignItems: 'flex-start', textAlign: 'left' }}>
                <Gift className="w-5 h-5 flex-shrink-0" style={{ marginTop: 1 }} />
                <span><strong>Teste grátis por 7 dias:</strong> <span style={{ fontWeight: 500 }}>experimente todos os recursos da plataforma gratuitamente após o cadastro, sem qualquer cobrança durante o período de testes.</span></span>
              </p>

              <ul style={{ listStyle: 'none', padding: 0, margin: '1.5rem 0', textAlign: 'left' }}>
                {FEATURES.map(([Icon, testId, text], i) => (
                  <li key={testId} data-testid={testId} style={{ ...li, ...(i === FEATURES.length - 1 ? { marginBottom: 0 } : {}), }}>
                    <Icon className="w-5 h-5 flex-shrink-0" style={ico} />
                    <span>{text}</span>
                  </li>
                ))}
              </ul>

              <Button 
                className="btn-primary-custom w-full"
                onClick={handleSelectPlan}
                data-testid="select-plan-btn"
                style={{
                  background: 'var(--lv-accent)',
                  padding: '1rem 2rem',
                  fontSize: '1.1rem',
                  fontWeight: '600'
                }}
              >
                Começar agora! (7 dias grátis)
              </Button>
              <a href="https://livrum.com.br/livrum-catalogo-demonstrativo" target="_blank" rel="noopener noreferrer" data-testid="pricing-demo-catalog-btn"
                style={{ display: 'block', marginTop: '0.75rem', padding: '0.85rem 1.5rem', borderRadius: 8, border: '1px solid var(--lv-ink)', color: 'var(--lv-ink)', fontWeight: 600, textDecoration: 'none', textAlign: 'center' }}>
                Ver catálogo de demonstração
              </a>
              
              <p style={{ 
                marginTop: '1rem', 
                color: 'var(--lv-muted)', 
                fontSize: '0.85rem' 
              }}>
                Cancele quando quiser
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Features adicionais */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1.5rem',
          marginTop: '3rem',
          maxWidth: '900px',
          margin: '3rem auto 0'
        }}>
          <div style={{
            textAlign: 'center',
            padding: '1.5rem',
            background: 'var(--lv-paper)',
            border: '1px solid var(--lv-line)',
            borderRadius: '12px'
          }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              borderRadius: '12px',
              background: 'var(--lv-accent-soft)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 0.75rem'
            }}>
              <Smartphone style={{ width: '24px', height: '24px', color: 'var(--lv-accent)' }} />
            </div>
            <h4 style={{ color: 'var(--lv-ink)', marginBottom: '0.5rem', fontWeight: '600' }}>Multiplataforma</h4>
            <p style={{ color: 'var(--lv-muted)', fontSize: '0.9rem', margin: 0 }}>
              Seus leitores acessam de qualquer dispositivo
            </p>
          </div>
          <div style={{
            textAlign: 'center',
            padding: '1.5rem',
            background: 'var(--lv-paper)',
            border: '1px solid var(--lv-line)',
            borderRadius: '12px'
          }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              borderRadius: '12px',
              background: 'var(--lv-accent-soft)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 0.75rem'
            }}>
              <Lock style={{ width: '24px', height: '24px', color: 'var(--lv-accent)' }} />
            </div>
            <h4 style={{ color: 'var(--lv-ink)', marginBottom: '0.5rem', fontWeight: '600' }}>Plataforma Segura</h4>
            <p style={{ color: 'var(--lv-muted)', fontSize: '0.9rem', margin: 0 }}>
              Proteção contra pirataria, cópia e captura de tela
            </p>
          </div>
          <div style={{
            textAlign: 'center',
            padding: '1.5rem',
            background: 'var(--lv-paper)',
            border: '1px solid var(--lv-line)',
            borderRadius: '12px'
          }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              borderRadius: '12px',
              background: 'var(--lv-accent-soft)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 0.75rem'
            }}>
              <DollarSign style={{ width: '24px', height: '24px', color: 'var(--lv-accent)' }} />
            </div>
            <h4 style={{ color: 'var(--lv-ink)', marginBottom: '0.5rem', fontWeight: '600' }}>Você no Controle</h4>
            <p style={{ color: 'var(--lv-muted)', fontSize: '0.9rem', margin: 0 }}>
              Defina o preço da sua assinatura
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
